import { getSymbol, Input, Select, Table } from "@xola/ui-kit";
import clsx from "clsx";
import { DemographicIcon, FormField } from "common";
import React from "react";
import { AdjustmentOperation, AdjustmentType, DemographicPrice } from "types";
import { calculatePriceWithAdjustment } from "../../../models/DemographicPrice";
import { isDefaultDemographic } from "../../../models/DemographicUnit";
import { getSeller } from "../../../utils/getSeller";
import { Currency } from "../../Common/Currency";

interface DemographicPriceItemProps {
    demographicPrice: DemographicPrice;
    index: number;
    perOuting?: boolean;
    errors?: any;
    setFieldValue: (key: string, value: any) => void;
}

export const DemographicPriceItem = ({
    demographicPrice,
    index,
    perOuting = false,
    errors,
    setFieldValue,
}: DemographicPriceItemProps) => {
    const seller = getSeller();
    const finalPrice = calculatePriceWithAdjustment(demographicPrice.price ?? 0, demographicPrice);

    const handleAdjustmentOperationChange = (index: number, value: AdjustmentOperation) => {
        if (value === AdjustmentOperation.FINAL) {
            setFieldValue(`${index}.adjustmentType`, AdjustmentType.ABSOLUTE);
        }

        setFieldValue(`${index}.adjustmentOperation`, value);
    };

    return (
        <Table.Row key={demographicPrice.template.id} className="justify-between">
            {perOuting ? null : (
                <Table.Cell className="w-1/4">
                    <FormField
                        label={null}
                        name={`${index}.name`}
                        className={clsx(!isDefaultDemographic(demographicPrice.catalogItem?.name) && "no-translate")}
                    >
                        <DemographicIcon className="mr-1" name={demographicPrice.catalogItem?.name ?? ""} />
                        {demographicPrice.catalogItem?.name ?? ""}
                    </FormField>
                </Table.Cell>
            )}
            <Table.Cell className="w-1/6">
                <FormField label={null} name={`${index}.price`}>
                    <Currency>{demographicPrice.price}</Currency>
                </FormField>
            </Table.Cell>
            <Table.Cell className="w-5/12">
                <FormField label={null} name={`${index}.adjustments`}>
                    <div className="flex">
                        <Input
                            required
                            type="number"
                            data-testid="demographic-price-item-input"
                            isError={errors?.[index]?.adjustmentAmount ?? false}
                            className="w-full rounded-tr-none rounded-br-none border-gray-light"
                            value={demographicPrice.adjustmentAmount}
                            onChange={(e: any) =>
                                setFieldValue(
                                    `${index}.adjustmentAmount`,
                                    e.target.value ? Number(e.target.value) : undefined,
                                )
                            }
                        />
                        <Select
                            data-testid="demographic-price-item-input-algorithm"
                            className="no-translate h-full w-full flex-shrink rounded-none rounded-tl-none border-r-0 border-l-0 border-gray-light"
                            value={demographicPrice.adjustmentType}
                            disabled={demographicPrice.adjustmentOperation === AdjustmentOperation.FINAL}
                            onChange={(e: any) => setFieldValue(`${index}.adjustmentType`, e.target.value)}
                        >
                            <option value={AdjustmentType.ABSOLUTE}>{getSymbol(seller.currency)}</option>
                            <option value={AdjustmentType.PERCENT}>%</option>
                        </Select>
                        <Select
                            className="h-full w-28 flex-grow rounded-tl-none rounded-bl-none"
                            value={demographicPrice.adjustmentOperation}
                            onChange={(e: any) =>
                                handleAdjustmentOperationChange(index, e.target.value as AdjustmentOperation)
                            }
                        >
                            <option value={AdjustmentOperation.MARKUP}>Markup</option>
                            <option value={AdjustmentOperation.MARKDOWN}>Markdown</option>
                            <option value={AdjustmentOperation.FINAL}>Final Price</option>
                        </Select>
                    </div>
                    {errors?.[index]?.adjustmentAmount ? (
                        <span className="text-sm font-bold text-danger">{errors?.[index]?.adjustmentAmount}</span>
                    ) : null}
                </FormField>
            </Table.Cell>
            <Table.Cell className="w-1/6">
                <FormField label={null} name={`${index}.adjustmentAmount`}>
                    <Currency>{finalPrice}</Currency>
                </FormField>
            </Table.Cell>
        </Table.Row>
    );
};
